'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
var _defineProperty = require('@babel/runtime/helpers/defineProperty');
var _objectWithoutProperties = require('@babel/runtime/helpers/objectWithoutProperties');
var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var _typeof = require('@babel/runtime/helpers/typeof');
var descendant = require('@chakra-ui/descendant');
var react = require('@chakra-ui/react');
var reactUtils = require('@chakra-ui/react-utils');
var utils = require('@chakra-ui/utils');
var React = require('react');
function _interopDefaultLegacy(e) {
  return e && typeof e === 'object' && 'default' in e ? e : {
    'default': e
  };
}
var _defineProperty__default = /*#__PURE__*/_interopDefaultLegacy(_defineProperty);
var _objectWithoutProperties__default = /*#__PURE__*/_interopDefaultLegacy(_objectWithoutProperties);
var _slicedToArray__default = /*#__PURE__*/_interopDefaultLegacy(_slicedToArray);
var _typeof__default = /*#__PURE__*/_interopDefaultLegacy(_typeof);
var _excluded = ["onChange", "defaultIndex", "index", "allowMultiple", "allowToggle"],
  _excluded2 = ["isDisabled", "isFocusable", "id"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty__default["default"](target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

/* -------------------------------------------------------------------------------------------------
 * Validate accordion and accordion item props, and emit warnings.
 * -----------------------------------------------------------------------------------------------*/

function allowMultipleWarning(props) {
  var index = props.index || props.defaultIndex;
  var condition = !utils.isUndefined(index) && !utils.isArray(index) && props.allowMultiple;
  utils.warn({
    condition: !!condition,
    message: "If 'allowMultiple' is passed, then 'index' or 'defaultIndex' must be an array. You passed: ".concat(_typeof__default["default"](index), ",")
  });
}
function allowMultipleAndAllowToggleWarning(props) {
  utils.warn({
    condition: !!(props.allowMultiple && props.allowToggle),
    message: "If 'allowMultiple' is passed, 'allowToggle' will be ignored. Either remove 'allowToggle' or 'allowMultiple' depending on whether you want multiple accordions visible or not"
  });
}
function focusableNotDisabledWarning(props) {
  utils.warn({
    condition: !!(props.isFocusable && !props.isDisabled),
    message: "Using only 'isFocusable', this prop is reserved for situations where you pass 'isDisabled' but you still want the element to receive focus (A11y). Either remove it or pass 'isDisabled' as well.\n    "
  });
}
function warnIfOpenAndDisabled(props) {
  utils.warn({
    condition: props.isOpen && !!props.isDisabled,
    message: 'Cannot open a disabled accordion item'
  });
}

/* -------------------------------------------------------------------------------------------------
 * Create context to track descendants and their indices
 * -----------------------------------------------------------------------------------------------*/

var _createDescendantCont = descendant.createDescendantContext(),
  _createDescendantCont2 = _slicedToArray__default["default"](_createDescendantCont, 4),
  AccordionDescendantsProvider = _createDescendantCont2[0],
  useAccordionDescendantsContext = _createDescendantCont2[1],
  useAccordionDescendants = _createDescendantCont2[2],
  useAccordionDescendant = _createDescendantCont2[3];
/**
 * useAccordion hook provides all the state and focus management logic
 * for accordion items.
 */
function useAccordion(props) {
  var onChangeProp = props.onChange,
    defaultIndex = props.defaultIndex,
    indexProp = props.index,
    allowMultiple = props.allowMultiple,
    _props$allowToggle = props.allowToggle,
    allowToggle = _props$allowToggle === void 0 ? true : _props$allowToggle,
    htmlProps = _objectWithoutProperties__default["default"](props, _excluded);

  // validate the props and `warn` if used incorrectly
  allowMultipleWarning(props);
  allowMultipleAndAllowToggleWarning(props);

  /**
   * Think of this as the register to each accordion item.
   * We used to manage focus between accordion item buttons.
   *
   * Every accordion item, registers their button refs in this context
   */
  var descendants = useAccordionDescendants();

  /**
   * This state is used to track the index focused accordion
   * button when click on the button, tab on the button, or
   * use the down/up arrow to navigate.
   */
  var _useState = React.useState(-1),
    _useState2 = _slicedToArray__default["default"](_useState, 2),
    focusedIndex = _useState2[0],
    setFocusedIndex = _useState2[1];

  /**
   * Reset focused index when accordion unmounts
   * or descendants change
   */
  react.useUnmountEffect(function () {
    setFocusedIndex(-1);
  });

  /**
   * Hook that manages the controlled and un-controlled state
   * for the accordion.
   */
  var _useControllableState = react.useControllableState({
      value: indexProp,
      defaultValue: function defaultValue() {
        if (allowMultiple) return defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : [];
        return defaultIndex !== null && defaultIndex !== void 0 ? defaultIndex : -1;
      },
      onChange: onChangeProp
    }),
    _useControllableState2 = _slicedToArray__default["default"](_useControllableState, 2),
    index = _useControllableState2[0],
    setIndex = _useControllableState2[1];

  /**
   * Gets the `isOpen` and `onChange` props for a child accordion item based on
   * the child's index.
   *
   * @param idx {number} The index of the child accordion item
   */
  var getAccordionItemProps = function getAccordionItemProps(idx) {
    var isOpen = false;
    if (idx !== null) {
      isOpen = utils.isArray(index) ? index.includes(idx) : index === idx;
    }
    var onChange = function onChange(innerIsOpen) {
      if (idx === null) return;
      if (allowMultiple && utils.isArray(index)) {
        var nextState = innerIsOpen ? utils.addItem(index, idx) : utils.removeItem(index, idx);
        setIndex(nextState);
      } else if (innerIsOpen) {
        setIndex(idx);
      } else if (allowToggle) {
        setIndex(-1);
      }
    };
    return {
      isOpen: isOpen,
      onChange: onChange
    };
  };
  return {
    index: index,
    setIndex: setIndex,
    htmlProps: htmlProps,
    getAccordionItemProps: getAccordionItemProps,
    focusedIndex: focusedIndex,
    setFocusedIndex: setFocusedIndex,
    descendants: descendants
  };
}
var _createContext = reactUtils.createContext({
    name: 'AccordionContext',
    errorMessage: 'useAccordionContext: `context` is undefined. Seems you forgot to wrap the accordion components in `<Accordion />`'
  }),
  _createContext2 = _slicedToArray__default["default"](_createContext, 2),
  AccordionProvider = _createContext2[0],
  useAccordionContext = _createContext2[1];
/**
 * useAccordionItem
 *
 * React hook that provides the open/close functionality
 * for an accordion item and its children
 */
function useAccordionItem(props) {
  var isDisabled = props.isDisabled,
    isFocusable = props.isFocusable,
    id = props.id,
    htmlProps = _objectWithoutProperties__default["default"](props, _excluded2);
  var _useAccordionContext = useAccordionContext(),
    getAccordionItemProps = _useAccordionContext.getAccordionItemProps,
    setFocusedIndex = _useAccordionContext.setFocusedIndex;
  var buttonRef = React.useRef(null);

  /**
   * Generate unique ids for all accordion item components (button and panel)
   */
  var _useIds = react.useIds(id, "accordion-button", "accordion-panel"),
    _useIds2 = _slicedToArray__default["default"](_useIds, 2),
    buttonId = _useIds2[0],
    panelId = _useIds2[1];
  focusableNotDisabledWarning(props);

  /**
   * Think of this as a way to register this accordion item
   * with its parent `useAccordion`
   */
  var _useAccordionDescenda = useAccordionDescendant({
      disabled: isDisabled && !isFocusable
    }),
    register = _useAccordionDescenda.register,
    index = _useAccordionDescenda.index,
    descendants = _useAccordionDescenda.descendants;
  var _getAccordionItemProp = getAccordionItemProps(index === -1 ? null : index),
    isOpen = _getAccordionItemProp.isOpen,
    onChange = _getAccordionItemProp.onChange;
  warnIfOpenAndDisabled({
    isOpen: isOpen,
    isDisabled: isDisabled
  });
  var onOpen = function onOpen() {
    onChange === null || onChange === void 0 ? void 0 : onChange(true);
  };
  var onClose = function onClose() {
    onChange === null || onChange === void 0 ? void 0 : onChange(false);
  };

  /**
   * Toggle the visibility of the accordion item
   */
  var onClick = React.useCallback(function () {
    onChange === null || onChange === void 0 ? void 0 : onChange(!isOpen);
    setFocusedIndex(index);
  }, [index, setFocusedIndex, isOpen, onChange]);

  /**
   * Manage keyboard navigation between accordion items.
   */
  var onKeyDown = React.useCallback(function (event) {
    var eventKey = utils.normalizeEventKey(event);
    var keyMap = {
      ArrowDown: function ArrowDown() {
        var next = descendants.nextEnabled(index);
        if (next) utils.focus(next.node);
      },
      ArrowUp: function ArrowUp() {
        var prev = descendants.prevEnabled(index);
        if (prev) utils.focus(prev.node);
      },
      Home: function Home() {
        var first = descendants.firstEnabled();
        if (first) utils.focus(first.node);
      },
      End: function End() {
        var last = descendants.lastEnabled();
        if (last) utils.focus(last.node);
      }
    };
    var action = keyMap[eventKey];
    if (action) {
      event.preventDefault();
      action(event);
    }
  }, [descendants, index]);

  /**
   * Since each accordion item's button still remains tabbable, let's
   * update the focusedIndex when it receives focus
   */
  var onFocus = React.useCallback(function () {
    setFocusedIndex(index);
  }, [setFocusedIndex, index]);
  var getButtonProps = React.useCallback(function () {
    var givenProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, givenProps), {}, {
      type: 'button',
      ref: reactUtils.mergeRefs(register, buttonRef, ref),
      id: buttonId,
      disabled: !!isDisabled,
      'aria-expanded': !!isOpen,
      'aria-controls': panelId,
      onClick: utils.callAllHandlers(givenProps.onClick, onClick),
      onFocus: utils.callAllHandlers(givenProps.onFocus, onFocus),
      onKeyDown: utils.callAllHandlers(givenProps.onKeyDown, onKeyDown)
    });
  }, [buttonId, isDisabled, isOpen, onClick, onFocus, onKeyDown, panelId, register]);
  var getPanelProps = React.useCallback(function () {
    var givenProps = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, givenProps), {}, {
      ref: ref,
      role: 'region',
      id: panelId,
      'aria-labelledby': buttonId,
      hidden: !isOpen
    });
  }, [buttonId, isOpen, panelId]);
  return {
    isOpen: isOpen,
    isDisabled: isDisabled,
    isFocusable: isFocusable,
    onOpen: onOpen,
    onClose: onClose,
    getButtonProps: getButtonProps,
    getPanelProps: getPanelProps,
    htmlProps: htmlProps
  };
}
exports.AccordionDescendantsProvider = AccordionDescendantsProvider;
exports.AccordionProvider = AccordionProvider;
exports.useAccordion = useAccordion;
exports.useAccordionContext = useAccordionContext;
exports.useAccordionDescendant = useAccordionDescendant;
exports.useAccordionDescendants = useAccordionDescendants;
exports.useAccordionDescendantsContext = useAccordionDescendantsContext;
exports.useAccordionItem = useAccordionItem;